import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion } from 'framer-motion';
import { faPause, faPlay } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import type { ComponentProps } from '@components-shared/src/stitches';
import { styled } from '@components-shared/src/stitches';
import { useIsSsr } from '@hooks/useIsSsr';

type PlayButtonProps = {
  /** wether button is in playing state */
  playing?: boolean;
  /** wether button is in loading state */
  loading?: boolean;
  /** wether button is elevated or not */
  elevated?: boolean;
  /** size of the button */
  size?: 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl';
  /** color of the button */
  color?: 'white' | 'black' | 'transparent';
  /** rounded */
  rounded?: boolean;
  /** wether button is rendered as a button or an a tag. Useful incase parent is a button. */
  pseudo?: boolean;
} & ComponentProps<typeof Button> &
  React.ComponentPropsWithRef<'button'>;

const BUTTON_SIZE = {
  xs: 32,
  s: 40,
  m: 48,
  l: 60,
  xl: 76,
  xxl: 120,
} as const;

export const PlayButton = React.forwardRef<HTMLButtonElement, PlayButtonProps>(function PlayButton(
  {
    playing,
    loading,
    color,
    size,
    elevated,
    disabled,
    onClick,
    pseudo = false,
    rounded = false,
    ...props
  },
  ref,
) {
  const iconColor =
    color === 'white'
      ? loading
        ? 'hsla(0%,0%,0%,0.3)'
        : 'black'
      : loading
      ? 'hsla(0,0%,100%,0.3)'
      : 'white';

  const Tag: React.ElementType = pseudo ? PseudoButton : Button;

  const ringSvgSize = BUTTON_SIZE[size as keyof typeof BUTTON_SIZE];

  const isSsr = useIsSsr();
  const disallowClick = isSsr || disabled || loading;
  const allowLoadingAnimation = loading && !disabled;

  return (
    <Tag
      data-testid="play-button"
      aria-disabled={disabled}
      disabled={disallowClick}
      onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (disallowClick) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        onClick?.(e);
      }}
      ref={ref}
      color={color}
      tabIndex={-1}
      {...props}
    >
      {/* Split button (trigger) and content (hover and tap animations) to ensure 
      click event fires when clicking outer edges of button */}
      <Content
        className="play-button-container"
        whileTap={{ scale: disallowClick || color === 'transparent' ? 1 : 0.8 }}
        whileHover={{ scale: disallowClick || color === 'transparent' ? 1 : 0.95 }}
        transition={{
          type: 'spring',
          damping: 45,
          stiffness: 1200,
        }}
        disabled={disabled}
        loading={loading}
        elevated={elevated}
        size={size}
        color={color}
        rounded={rounded}
        ssr={isSsr}
      >
        <IconWrapper
          style={{
            // these fontawesome icons are are not optically centred so its obvious when rounded
            // the margin below is to correct optically for the play button
            marginLeft: !playing && rounded ? '4%' : 0,
          }}
          transition={{
            type: 'spring',
            damping: 100,
            stiffness: 2600,
          }}
          animate={{
            color:
              color === 'black'
                ? loading
                  ? 'hsla(0,0%,100%,0.3)'
                  : 'hsla(0,0%,100%,1)'
                : loading
                ? 'hsla(0,0%,0%,0.3)'
                : 'hsla(0,0%,0%,1)',
          }}
        >
          {playing ? (
            <FontAwesomeIcon className="pause-icon" icon={faPause} color={iconColor} />
          ) : (
            <FontAwesomeIcon className="play-icon" icon={faPlay} color={iconColor} />
          )}
        </IconWrapper>

        <svg
          className="loading-ring"
          width={ringSvgSize}
          height={ringSvgSize}
          viewBox={`0 0 ${ringSvgSize} ${ringSvgSize}`}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <motion.rect
            x={size === 'xxl' ? 28 : 4}
            y={size === 'xxl' ? 28 : 4}
            width={size === 'xxl' ? 65 : ringSvgSize - 8}
            height={size === 'xxl' ? 65 : ringSvgSize - 8}
            rx={rounded ? BUTTON_SIZE[size as keyof typeof BUTTON_SIZE] : '4'}
            strokeWidth="4"
            stroke="#fff"
            strokeLinejoin="round"
            animate={{
              pathLength: allowLoadingAnimation ? 0.25 : 0,
              pathSpacing: allowLoadingAnimation ? 0.75 : 0,
              pathOffset: allowLoadingAnimation ? [0, 2] : 0,
              stroke:
                color === 'white'
                  ? allowLoadingAnimation
                    ? 'hsla(0,0%,0%,1)'
                    : 'hsla(0,0%,0%,0)'
                  : allowLoadingAnimation
                  ? 'hsla(0,0%,100%,1)'
                  : 'hsla(0,0%,100%,0)',
              strokeWidth: allowLoadingAnimation ? ringSvgSize * 0.03 : 1,
              transition: {
                stroke: {
                  duration: allowLoadingAnimation ? 0.5 : 0,
                },
                pathSpacing: {
                  duration: allowLoadingAnimation ? 0.5 : 0,
                },
                strokeWidth: {
                  duration: allowLoadingAnimation ? 0.5 : 0,
                },
                pathLength: {
                  duration: allowLoadingAnimation ? 0.5 : 0,
                },
                pathOffset: {
                  duration: allowLoadingAnimation ? 1.5 : 0,
                  repeat: allowLoadingAnimation ? Infinity : 0,
                },
              },
            }}
          />
        </svg>
      </Content>
    </Tag>
  );
});

const Button = styled(motion.button, {
  position: 'relative',
  borderRadius: '$sm',
});

const PseudoButton = styled(motion.div, {
  position: 'relative',
});

const Content = styled(motion.div, {
  borderRadius: 6,
  padding: '0px, 1px, 0px, 1px',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  variants: {
    disabled: {
      true: {
        cursor: 'not-allowed',
      },
    },
    ssr: {
      true: {
        cursor: 'not-allowed',
      },
    },
    loading: {
      true: {
        cursor: 'progress',
      },
      false: {
        '.loading-ring': {
          display: 'none',
        },
      },
    },
    rounded: {
      true: {
        borderRadius: '100%',
      },
    },
    size: {
      xs: {
        width: BUTTON_SIZE['xs'],
        height: BUTTON_SIZE['xs'],
        '.play-icon, .pause-icon': {
          fontSize: 14,
        },
      },
      s: {
        width: BUTTON_SIZE['s'],
        height: BUTTON_SIZE['s'],
        '.play-icon, .pause-icon': {
          fontSize: 16,
        },
      },
      m: {
        width: BUTTON_SIZE['m'],
        height: BUTTON_SIZE['m'],
        '.play-icon, .pause-icon': {
          fontSize: 20,
        },
      },
      l: {
        width: BUTTON_SIZE['l'],
        height: BUTTON_SIZE['l'],
        '.play-icon, .pause-icon': {
          fontSize: 24,
        },
      },
      xl: {
        width: BUTTON_SIZE['xl'],
        height: BUTTON_SIZE['xl'],
        '.play-icon, .pause-icon': {
          fontSize: 32,
        },
      },
      xxl: {
        width: BUTTON_SIZE['xxl'],
        height: BUTTON_SIZE['xxl'],
        '.play-icon, .pause-icon': {
          fontSize: 32,
        },
      },
    },
    color: {
      white: {
        backgroundColor: '$white',
        opacity: 0.9,
        '&:hover': {
          opacity: 1,
        },
      },
      black: {
        backgroundColor: '$neutral800',
        '&:hover': {
          backgroundColor: '$black',
        },
      },
      transparent: {
        backgroundColor: 'rgba(0,0,0,0.4)',
        '&:hover': {
          backgroundColor: 'rgba(0,0,0,0.6)',
        },
      },
    },
    elevated: {
      true: {
        lift: 'high',
      },
    },
  },

  compoundVariants: [
    {
      loading: true,
      rounded: true,
      css: {
        borderRadius: '9999px',
      },
    },
    {
      loading: true,
      disabled: true,
      css: {
        '.loading-ring': {
          display: 'none',
        },
      },
    },
    {
      color: 'black',
      disabled: true,
      css: {
        opacity: 0.3,
        '&:hover': {
          backgroundColor: '$neutral800',
        },
      },
    },
    {
      color: 'white',
      disabled: true,
      css: {
        opacity: 0.3,
        '&:hover': {
          backgroundColor: '$white',
          opacity: 0.3,
        },
      },
    },
  ],

  defaultVariants: {
    color: 'black',
    size: 'xl',
  },
});

const IconWrapper = styled(motion.div, {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});
